import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { ProfileTabType } from '../ProfileSidebar/ProfileSidebar';
import { useLoyaltyContext } from '~/shared/hooks/loyalty';
import { useMemberAccountInformationSilent } from '../../hooks/useMemberAccountInformationSilent';
import ProfileOverview from '../ProfileTabs/ProfileOverview';
import BonusRewards from '../ProfileTabs/BonusRewards';
import Vouchers from '../ProfileTabs/Vouchers';
import Purchases from '../ProfileTabs/Purchases';
import ProfileSettings from '../ProfileTabs/ProfileSettings';
import styles from './ProfileContent.module.scss';

const ProfileContent: React.FC = () => {
    const router = useRouter();
    const { canFetchLoyaltyData } = useLoyaltyContext();
    // Check if the query fails to determine if endpoint is actually available and use silent version to suppress error toasts
    const {
        isError: isMemberAccountError,
        isLoading: isMemberAccountLoading,
        isFetching: isMemberAccountFetching,
        data: memberAccountData,
    } = useMemberAccountInformationSilent();
    const tab = (router.query.tab as ProfileTabType) || 'overview';

    // Determine if loyalty features should be shown
    const isQuerySettled = !isMemberAccountLoading && !isMemberAccountFetching;
    const shouldShowLoyaltyTabs =
        canFetchLoyaltyData && isQuerySettled && !isMemberAccountError && !!memberAccountData;

    // Redirect to overview if user tries to access a disabled loyalty tab
    useEffect(() => {
        if (!shouldShowLoyaltyTabs) {
            const loyaltyTabs: ProfileTabType[] = ['bonusrewards', 'vouchers', 'purchases'];
            if (loyaltyTabs.includes(tab)) {
                router.replace('/my-profile', undefined, { scroll: false });
            }
        }
    }, [shouldShowLoyaltyTabs, tab, router]);

    // Render the appropriate tab content based on the query parameter
    const renderTabContent = () => {
        // If loyalty endpoint is unavailable and user is on a loyalty tab, show overview
        if (!shouldShowLoyaltyTabs && ['bonusrewards', 'vouchers', 'purchases'].includes(tab)) {
            return <ProfileOverview />;
        }

        switch (tab) {
            case 'bonusrewards':
                return <BonusRewards />;
            case 'vouchers':
                return <Vouchers />;
            case 'purchases':
                return <Purchases />;
            case 'settings':
                return <ProfileSettings />;
            case 'overview':
            default:
                return <ProfileOverview />;
        }
    };

    return <div className={styles.content}>{renderTabContent()}</div>;
};

export default ProfileContent;
